import { tonesFromBrand } from "./tones-from-brand";

type PaletteHues = Record<string, string>;

interface Rgb {
    r: number;
    g: number;
    b: number;
}

const WHITE = "#ffffff";
const BLACK = "#000000";

/** WCAG AA for normal-size text, which is what button and chip labels are. */
const MIN_LABEL_CONTRAST = 4.5;

/** Material 3 state layer opacities. */
const HOVER_STATE_OPACITY = 8;
const PRESSED_STATE_OPACITY = 12;

/** How much brand shows in a container. Low enough that the container still reads as a surface. */
const CONTAINER_BRAND_RATIO = 25;

/**
 * Applique les variables CSS de thème sur document.documentElement.
 * API cible : tenantContext.branding.cssVars
 */
export function applyThemeVarsToRoot(vars: Record<string, string>): void {
    const root = document.documentElement.style;
    for (const [varName, value] of Object.entries(vars)) {
        root.setProperty(varName, value);
    }
}

/**
 * Emits the `--sys-primary-*` tones generated from the tenant's brand color.
 *
 * Only `primary` is tenant-specific. The other six palettes are identical for every tenant and are
 * already compiled into `--sys-*` from `$_palettes` in `m3-theme.scss`, so there is nothing to
 * override for them at runtime.
 */
export function primaryThemeVars(brandColor: string): Record<string, string> {
    const vars: Record<string, string> = {};

    for (const [tone, value] of Object.entries(tonesFromBrand(brandColor))) {
        vars[`--sys-primary-${tone}`] = value;
    }

    return vars;
}

/**
 * Applies every palette from the API blob.
 *
 * Only used for tenants that have no brand color, where the stored palettes are still the best
 * available source. Once every tenant is backfilled this can go, along with `themeVariables`.
 */
export function paletteToThemeVars(palette: Record<string, PaletteHues>): Record<string, string> {
    const vars: Record<string, string> = {};

    for (const key of Object.keys(palette)) {
        const hues = palette[key];
        if (!hues) {
            continue;
        }

        for (const [hue, value] of Object.entries(hues)) {
            vars[`--sys-${key}-${hue}`] = value;
        }
    }

    return vars;
}

function parseHexColor(value: string | null | undefined): Rgb | null {
    if (value === null || value === undefined) {
        return null;
    }

    const hex = value.trim().replace(/^#/, "");
    const expanded =
        hex.length === 3
            ? hex
                  .split("")
                  .map((char) => `${char}${char}`)
                  .join("")
            : hex;

    if (!/^[0-9a-f]{6}$/i.test(expanded)) {
        return null;
    }

    return {
        r: parseInt(expanded.slice(0, 2), 16),
        g: parseInt(expanded.slice(2, 4), 16),
        b: parseInt(expanded.slice(4, 6), 16)
    };
}

function normalizeHexColor(value: string | null | undefined): string | null {
    const rgb = parseHexColor(value);
    if (!rgb) {
        return null;
    }

    const channel = (n: number): string => n.toString(16).padStart(2, "0");
    return `#${channel(rgb.r)}${channel(rgb.g)}${channel(rgb.b)}`;
}

function toLinearChannel(channel: number): number {
    const normalized = channel / 255;
    return normalized <= 0.04045 ? normalized / 12.92 : ((normalized + 0.055) / 1.055) ** 2.4;
}

function relativeLuminance(rgb: Rgb): number {
    return 0.2126 * toLinearChannel(rgb.r) + 0.7152 * toLinearChannel(rgb.g) + 0.0722 * toLinearChannel(rgb.b);
}

/** WCAG 2.1 contrast ratio, from 1 (identical) to 21 (black on white). */
export function contrastRatio(a: string, b: string): number {
    const rgbA = parseHexColor(a);
    const rgbB = parseHexColor(b);
    if (!rgbA || !rgbB) {
        return 1;
    }

    const luminanceA = relativeLuminance(rgbA);
    const luminanceB = relativeLuminance(rgbB);
    const lighter = Math.max(luminanceA, luminanceB);
    const darker = Math.min(luminanceA, luminanceB);

    return (lighter + 0.05) / (darker + 0.05);
}

/**
 * CIE L*, which is what Material 3 calls a palette's "tone". Lets us reason about a brand color's
 * position in its own ramp without pulling in a full HCT implementation.
 */
export function toneOf(hex: string): number {
    const rgb = parseHexColor(hex);
    if (!rgb) {
        return 0;
    }

    const luminance = relativeLuminance(rgb);
    return luminance <= 216 / 24389 ? (luminance * 24389) / 27 : Math.cbrt(luminance) * 116 - 16;
}

/**
 * Picks the most readable label color for `brand`.
 *
 * A brand color can land anywhere in the tonal range — the five current tenants span tone 49 to 95 —
 * so which direction a label has to go in is not fixed. Light brands take a dark label and dark
 * brands take a light one, matching Material 3.
 *
 * Within that direction the candidates are tried most-brand-harmonious first and the first one that
 * clears AA wins, rather than simply taking the highest contrast. Maximizing contrast would always
 * land on pure black or white, since a ramp's tone 0 and 100 are exactly those; the low tones are a
 * dark tint of the brand's own hue, which reads as designed rather than stark while still clearing
 * AA comfortably for every current tenant.
 *
 * The result is guaranteed to clear AA for any brand color, so callers do not need to re-check it.
 */
export function pickOnBrandColor(brand: string, primary: PaletteHues): string {
    const tinted = [primary["10"], primary["20"], primary["0"]];
    // Ordered most brand-harmonious first, then the guaranteed-legible extremes as a backstop.
    const byPreference = (toneOf(brand) < 60 ? [WHITE, ...tinted, BLACK] : [...tinted, BLACK, WHITE])
        .map((candidate) => normalizeHexColor(candidate))
        .filter((candidate): candidate is string => candidate !== null);

    // A readable candidate always exists: black and white are always in the list, and the worst any
    // color can do is sit on the crossover where both score sqrt(21), or 4.58:1.
    return byPreference.find((candidate) => contrastRatio(candidate, brand) >= MIN_LABEL_CONTRAST) ?? BLACK;
}

/**
 * Builds the `--brand` token set that the Material role tokens are routed through.
 *
 * `primary` is the resolved ramp: generated from the brand color when there is one, otherwise the
 * stored blob. `brandColor` itself is the only accurate source for `--brand`, because a tonal ramp
 * normalizes lightness and none of its tones is the tenant's actual brand. When it is absent — an
 * old backend, a cache entry predating the field, or a tenant nobody has filled in — this falls
 * back to `primary["50"]`, reproducing the previous behaviour rather than claiming to be correct.
 *
 * Hover and pressed are Material state layers mixed against `--brand` itself rather than picked out
 * of the ramp, because a ramp can be seeded from a different hex than the brand color and would
 * drift in hue. Mixing toward `--on-brand` also gets the direction right for free: light brands
 * darken, dark brands lighten.
 */
export function brandThemeVars(primary: PaletteHues, brandColor: string | null | undefined): Record<string, string> {
    const brand = normalizeHexColor(brandColor) ?? normalizeHexColor(primary["50"]);
    if (brand === null) {
        return {};
    }

    const onBrand = pickOnBrandColor(brand, primary);

    return {
        "--brand": brand,
        "--on-brand": onBrand,
        "--brand-hover": `color-mix(in oklch, ${onBrand} ${HOVER_STATE_OPACITY}%, ${brand})`,
        "--brand-pressed": `color-mix(in oklch, ${onBrand} ${PRESSED_STATE_OPACITY}%, ${brand})`,
        // The dashboard is a dark theme, so a container is the brand tinted into the dark surface
        // and its label is a light tone, not the other way around. The surface is referenced as a
        // CSS variable so the constant stays owned by `m3-theme.scss` alone.
        "--brand-container": `color-mix(in oklch, ${brand} ${CONTAINER_BRAND_RATIO}%, var(--sys-neutral-20))`,
        "--on-brand-container": normalizeHexColor(primary["90"]) ?? WHITE
    };
}
